import type { PlanTask } from '@/types/domain';

/**
 * 计划节点的完成度：一份判据，阶段工作表、节点抽屉、计划甘特三处都读它（0930）。
 *
 * Tony 0930：「完成度也可以填写……同步到计划甘特，比如甘特图上标注对应的完成度」。
 * 两页读的本来就是同一张 pm_plan_nodes，数据不用另外同步；差的是两件事：
 * · 甘特条上只有一行小字，而且只有填过的节点才有。线上在跑项目 4106 个节点里
 *   填过完成度的 906 个全是 100%——都是标完成时后端顺手补满的，没人手填过中间值；
 * · 甘特默认只列阶段行，阶段自己的完成度基本是空的，下面的活动做到哪一步看不出来。
 *
 * 口径：
 * · 做完（状态完成 / 已批准，或填了实际完成）= 100，和后端 nodeIsDoneSql 同一条；
 * · 不适用、已取消的节点不算，也不进上级的汇总；
 * · 有下级活动的节点，按下级的**末端活动**逐项平均（每项一票，不按工期加权——
 *   没排期的活动按工期算权重是 0，会被静默丢掉）；没填的按 0 算；
 * · 节点自己被人标成完成的，以人标的为准，算 100（它下面的末端活动在上级汇总里也按做完算）。
 */

const DONE_STATUSES: ReadonlySet<string> = new Set(['completed', 'approved']);
const OUT_STATUSES: ReadonlySet<string> = new Set(['not_applicable', 'cancelled']);

type ProgressSubject = Pick<PlanTask, 'status' | 'actualEnd' | 'progress' | 'applicable'>;

/** 做完了：状态完成 / 已批准，或填了实际完成日期 */
export function isPlanNodeDone(task: Pick<PlanTask, 'status' | 'actualEnd'>): boolean {
  return DONE_STATUSES.has(task.status ?? '') || Boolean(task.actualEnd);
}

/** 参与完成度统计：适用，且没被标成不适用 / 已取消 */
export function isPlanNodeCounted(task: Pick<PlanTask, 'status' | 'applicable'>): boolean {
  return task.applicable !== false && !OUT_STATUSES.has(task.status ?? '');
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, Math.round(value)));
}

export interface NodeProgress {
  /** 0–100 的整数；null = 不参与统计，或末端活动既没做完也没人填过 */
  value: number | null;
  /** done 做完了 · own 自己填的 · rollup 按下级汇总 · none 没有数 */
  source: 'done' | 'own' | 'rollup' | 'none';
  /** 汇总时才有：参与的末端活动数、其中做完的、报过进度（没做完、填了大于 0）的 */
  leaves?: number;
  doneLeaves?: number;
  reportedLeaves?: number;
}

/** 末端活动自己的完成度：做完 100，否则自己填的数，没填 null */
function leafValue(task: ProgressSubject): number | null {
  if (isPlanNodeDone(task)) return 100;
  return typeof task.progress === 'number' ? clampPercent(task.progress) : null;
}

function keyOf(task: Pick<PlanTask, 'id' | 'activityId'>): string {
  return String(task.activityId ?? task.id);
}

/**
 * 算整张计划的完成度，键是节点 id（String）。
 *
 * 层级按 parentId → 上级的 activityId 串，和两页画树的方式一样。
 * 一次建好孩子表、按节点记忆末端统计，几百个节点每次重算也就一遍遍历。
 */
export function buildProgressIndex(tasks: readonly PlanTask[]): Map<string, NodeProgress> {
  const children = new Map<string, PlanTask[]>();
  for (const task of tasks) {
    if (task.parentId === null || task.parentId === undefined) continue;
    const parent = String(task.parentId);
    if (!parent || parent === '0') continue;
    const list = children.get(parent) ?? [];
    list.push(task);
    children.set(parent, list);
  }

  interface LeafStats { count: number; sum: number; done: number; reported: number }
  const memo = new Map<string, LeafStats>();
  /** 这个节点下面参与统计的末端活动；没有参与统计的下级时 count 是 0 */
  const leafStats = (task: PlanTask, trail: Set<string>): LeafStats => {
    const key = keyOf(task);
    const cached = memo.get(key);
    if (cached) return cached;
    const stats: LeafStats = { count: 0, sum: 0, done: 0, reported: 0 };
    if (trail.has(key)) return stats;   // 数据里绕成圈时别死循环
    trail.add(key);
    for (const child of children.get(key) ?? []) {
      if (!isPlanNodeCounted(child)) continue;
      const below = leafStats(child, trail);
      if (below.count > 0) {
        // 子阶段被人标成完成：它下面的活动在这里都按做完算，和它自己显示 100 对得上
        const childDone = isPlanNodeDone(child);
        stats.count += below.count;
        stats.sum += childDone ? below.count * 100 : below.sum;
        stats.done += childDone ? below.count : below.done;
        stats.reported += childDone ? 0 : below.reported;
        continue;
      }
      const value = leafValue(child);
      stats.count += 1;
      stats.sum += value ?? 0;
      if (isPlanNodeDone(child)) stats.done += 1;
      else if ((value ?? 0) > 0) stats.reported += 1;
    }
    trail.delete(key);
    memo.set(key, stats);
    return stats;
  };

  const index = new Map<string, NodeProgress>();
  for (const task of tasks) {
    const id = String(task.id);
    if (!isPlanNodeCounted(task)) {
      index.set(id, { value: null, source: 'none' });
      continue;
    }
    if (isPlanNodeDone(task)) {
      index.set(id, { value: 100, source: 'done' });
      continue;
    }
    const stats = leafStats(task, new Set());
    if (stats.count > 0) {
      index.set(id, {
        value: clampPercent(stats.sum / stats.count),
        source: 'rollup',
        leaves: stats.count,
        doneLeaves: stats.done,
        reportedLeaves: stats.reported,
      });
      continue;
    }
    const own = leafValue(task);
    index.set(id, own === null ? { value: null, source: 'none' } : { value: own, source: 'own' });
  }
  return index;
}

/** 格子里显示的字：60% / — */
export function progressText(progress: NodeProgress | null | undefined): string {
  return progress?.value === null || progress?.value === undefined ? '—' : `${progress.value}%`;
}

/** 悬停说明：这个数是怎么来的 */
export function progressTitle(progress: NodeProgress | null | undefined): string {
  if (!progress) return '';
  if (progress.source === 'done') return '已完成';
  if (progress.source === 'rollup') {
    return `按下面 ${progress.leaves} 项活动平均：${progress.doneLeaves} 项已完成、`
      + `${progress.reportedLeaves} 项报了进度，其余按 0 算`;
  }
  if (progress.source === 'own') return '负责人填报的完成度';
  return '还没人填完成度';
}

/* ---------- 抽屉 / 表格里「完成度 · 实际完成 · 状态」三格互相跟 ---------- */

/**
 * 状态能怎么转，和后端 projects.rules.ts 的 STATUS_TRANSITIONS 逐字相同
 * （plan-progress.spec.ts 读后端源码钉着）。后端按「库里的状态 → 这次要的状态」判，
 * 所以表单里自动跟着改的状态也必须从**打开抽屉时的状态**能一步转过去，否则保存时吃 400。
 */
export const PLAN_STATUS_TRANSITIONS: Readonly<Record<string, readonly string[]>> = {
  '': ['pending', 'in_progress', 'completed', 'not_applicable'],
  pending: ['in_progress', 'completed', 'blocked', 'not_applicable'],
  in_progress: ['completed', 'blocked', 'rejected', 'not_applicable'],
  blocked: ['in_progress', 'completed', 'not_applicable'],
  completed: ['approved', 'rejected', 'in_progress'],
  approved: ['in_progress'],
  rejected: ['in_progress', 'not_applicable'],
  not_applicable: ['pending', 'in_progress'],
};

export const PLAN_STATUS_LABEL: Readonly<Record<string, string>> = {
  '': '未标状态', pending: '待处理', in_progress: '进行中', blocked: '阻塞', completed: '已完成',
  approved: '已批准', rejected: '已退回', not_applicable: '不适用', cancelled: '已取消',
};

/**
 * 抽屉「状态」下拉里给哪几个：当前状态 + 能一步转过去的。
 * 批准 / 退回只能由审批链产生，不给手选；不适用在「更多设置」的「是否适用」里改，
 * 和表格上的开关同一处，不在这里再开一个口。
 */
export function planStatusOptions(current: string): string[] {
  const allowed = (PLAN_STATUS_TRANSITIONS[current] ?? PLAN_STATUS_TRANSITIONS[''])
    .filter((status) => !['approved', 'rejected', 'not_applicable'].includes(status));
  return [current, ...allowed.filter((status) => status !== current)];
}

/** 抽屉表单里这三格的值：日期是 YYYY-MM-DD，空串 = 没填 */
export interface ProgressDraft {
  progress: number | null;
  actual: string;
  status: string;
}

export type ProgressEdit =
  | { field: 'progress'; value: number | null }
  | { field: 'actual'; value: string }
  | { field: 'status'; value: string };

/**
 * 人改了其中一格，另外两格怎么跟。base 是打开抽屉时的值。
 *
 * 只动表单里的值、不直接存：保存前人看得见、改得回去；后端的判据一条没变。
 * · 完成度拉到 100 = 做完了：状态改成完成，实际完成没填就先填（原来有日期用原来的，没有填今天）；
 * · 已完成的往回拉 = 重新打开：状态回到进行中，实际完成清掉——
 *   只改数字不清日期的话，库里按「填了实际完成」照样判它做完了；
 * · 待处理的报了进度（大于 0）= 在做了：状态改成进行中；
 * · 填了实际完成 = 做完了：完成度 100、状态完成；清掉已完成节点的实际完成 = 重新打开；
 * · 手选「完成」：完成度补满 100、实际完成没填就先填；从完成改回别的状态 = 重新打开。
 *
 * 自动改的状态必须是从 base 一步能到的（见 PLAN_STATUS_TRANSITIONS）；到不了就不动——
 * 比如「已退回」不能直接转成完成，与其保存时报错，不如表单里状态原样留着。
 */
export function linkProgressDraft(
  draft: ProgressDraft,
  edit: ProgressEdit,
  base: ProgressDraft,
  today: string,
): ProgressDraft {
  const next: ProgressDraft = { ...draft };
  const wasDone = DONE_STATUSES.has(draft.status);
  const reachable = (status: string): boolean => status === base.status
    || (PLAN_STATUS_TRANSITIONS[base.status] ?? PLAN_STATUS_TRANSITIONS['']).includes(status);
  const finish = (): void => {
    if (!DONE_STATUSES.has(next.status)) {
      // 原来就是完成 / 已批准的，退回去再拉满 = 还原成原来那个状态
      if (DONE_STATUSES.has(base.status)) next.status = base.status;
      else if (reachable('completed')) next.status = 'completed';
    }
    if (DONE_STATUSES.has(next.status)) next.actual = next.actual || base.actual || today;
  };
  const reopen = (): void => {
    if (DONE_STATUSES.has(next.status)) {
      next.status = reachable('in_progress') ? 'in_progress' : base.status;
    }
    next.actual = '';
  };

  if (edit.field === 'progress') {
    next.progress = edit.value === null ? null : clampPercent(edit.value);
    if (next.progress === 100) {
      finish();
      return next;
    }
    if (wasDone) reopen();
    if ((next.progress ?? 0) > 0 && (next.status === 'pending' || next.status === '') && reachable('in_progress')) {
      next.status = 'in_progress';
    }
    return next;
  }

  if (edit.field === 'actual') {
    next.actual = edit.value;
    if (edit.value) {
      next.progress = 100;
      finish();
    } else if (wasDone) {
      reopen();
      if (next.progress === 100) next.progress = null;
    }
    return next;
  }

  next.status = edit.value;
  if (DONE_STATUSES.has(edit.value)) {
    next.progress = 100;
    next.actual = next.actual || base.actual || today;
  } else if (wasDone) {
    next.actual = '';
    if (next.progress === 100) next.progress = null;
  }
  return next;
}

/** 上海时区的今天，YYYY-MM-DD（和后端 shanghaiToday 同一个算法：早上 8 点前不能算成昨天） */
export function shanghaiToday(now: Date = new Date()): string {
  return new Date(now.getTime() + 8 * 60 * 60 * 1000).toISOString().slice(0, 10);
}
